import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { of, map, mergeMap, catchError, exhaustMap, tap, withLatestFrom, switchMap, filter } from 'rxjs';
import { AuthApiService } from '../../services/auth-api.service';
import {
  Login,
  LoginSuccess,
  LoginFail,
  LoadProfile,
  LoadProfileSuccess,
  LoadProfileFail,
  EditProfile,
  LoadCountries,
  LoadCountriesSuccess,
  LoadCountriesFail,
  Logout,
  EditProfileSuccess,
  EditProfileFail,
  ChangePassword,
  ChangePasswordSuccess,
  ChangePasswordFail,
  LoginWithMultiFactor,
  SetResendToken,
  ResendVerificationCode,
} from '../actions/auth.actions';
import { Router } from '@angular/router';
import { Session } from '../../interfaces/session.interface';
import { Profile } from '../../interfaces/profile.interface';
import { Country } from '../../interfaces/country.interface';
import { State } from '../index';
import { Store } from '@ngrx/store';
import { selectCountriesLoaded } from '../selectors/countries.selectors';
import Backendless from 'backendless';
import { RealTimeService } from '@core/services/real-time.service';
import { MultiFactorAuth } from '@auth/interfaces/multi-factor-auth.interface';
import { selectResendToken, selectUsername } from '../selectors/login.selectors';

@Injectable()
export class AuthEffects {
  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(Login),
      concatLatestFrom(() => this.store.select(selectUsername)),
      map(([action, username]) => {
        // If username is already set, use it; otherwise, use the action's username
        const effectiveUsername = username || action.username;
        // return a new action with the effective username
        return Login({ username: effectiveUsername as string, password: action.password, verificationCode: action.verificationCode });
      }),
      exhaustMap((action) =>
        this.authApiService.login(action.username, action.password, action.verificationCode).pipe(
          mergeMap((session: Session | MultiFactorAuth) => {
            if ((session as MultiFactorAuth).mfaRequired) {
              // redirect to MFA verification page
              return of(LoginWithMultiFactor({ username: action.username, resendToken: (session as MultiFactorAuth).resendToken }));
            }
            Backendless.LocalCache.set('user-token', (session as Session).userToken);
            return [LoginSuccess({ session: session as Session }), LoadProfile(), LoadCountries()];
          }),
          catchError((error) => of(LoginFail({ error })))
        )
      )
    );
  });

  loginWithMultiFactor$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginWithMultiFactor),
        tap((_) => {
          // Navigate to the multi-factor authentication page
          this.router.navigateByUrl('/2fa');
        })
      );
    },
    { dispatch: false }
  );

  loginSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(LoginSuccess),
        tap(() => {
          this.realTimeService.initRealTime();
          this.router.navigateByUrl('/dashboard');
        })
      );
    },
    { dispatch: false }
  );

  loadProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoadProfile),
      exhaustMap((action) => this.authApiService.getProfile().pipe(map((profile: Profile) => LoadProfileSuccess({ profile })))),
      catchError((error) => {
        return of(LoadProfileFail({ error }));
      })
    );
  });

  editProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EditProfile),
      exhaustMap((action) =>
        this.authApiService.editProfile(action.changes).pipe(
          mergeMap((profile) => {
            // console.log('EditProfile effect', profile);
            if (profile && 'resendToken' in profile && profile.resendToken) {
              return [SetResendToken({ resendToken: profile.resendToken })];
            }
            return [EditProfileSuccess({ profile })];
          })
        )
      ),
      catchError((error) => {
        return of(EditProfileFail({ error }));
      })
    );
  });

  logout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(Logout),
        tap(() => {
          Backendless.LocalCache.remove('user-token');
          this.realTimeService.stopRealTime();
          this.router.navigateByUrl('/sign-in');
        })
      );
    },
    { dispatch: false }
  );

  changePassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChangePassword),
      exhaustMap((action) =>
        this.authApiService.changePassword(action.password).pipe(
          map((profile: Profile) => {
            // Backendless.LocalCache.set('user-token', session.userToken);
            return ChangePasswordSuccess({ profile });
          }),
          catchError((error) => of(ChangePasswordFail({ error })))
        )
      )
    );
  });

  loadCountries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoadCountries),
      concatLatestFrom(() => this.store.select(selectCountriesLoaded)),
      filter(([action, hasLoaded]) => !hasLoaded), // only continue if hasLoaded is false
      switchMap((action) => {
        return this.authApiService.getCountries().pipe(
          map((countries: Country[]) => {
            return LoadCountriesSuccess({ countries });
          }),
          catchError((error) => {
            return of(LoadCountriesFail({ error }));
          })
        );
      })
    );
  });

  resendVerificationCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ResendVerificationCode),
      concatLatestFrom(() => this.store.select(selectResendToken)),
      filter(([action, resendToken]) => !!resendToken), // only continue if resendToken is not null
      switchMap(([action, resendToken]) => {
        return this.authApiService.resendVerificationCode(resendToken as string).pipe(
          map(({ resendToken }) => {
            return SetResendToken({ resendToken });
          }),
          catchError((error) => {
            return of(ChangePasswordFail({ error }));
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private authApiService: AuthApiService,
    private router: Router,
    private store: Store<State>,
    private realTimeService: RealTimeService
  ) {}
}
